import { MAIN_CHAT_MODE } from '@littlebird/sdk';

import {
  actionCardEventSnapshot,
  trackCardChatClicked,
  trackCardCompletedSnapshot,
} from '@/features/action-cards-view/analytics';
import { actionCardAnalyticsContext } from '@/features/action-cards/analytics';
import { actionCardCommands } from '@/features/action-cards/commands';
import { performAttributedSend } from '@/features/chat/chat-send';
import type { useTabs } from '@/features/tabs';
import { logError } from '@/lib/errorLogging';
import { littlebirdSdk } from '@/lib/sdk-init';
import { trackEvent } from '@/utils/analytics';
import { NEW_CHAT_ID } from '@/utils/constants';

import type { ChatPromptCardEntry } from '@littlebird/action-cards';

type OpenTab = ReturnType<typeof useTabs>['openTab'];

// The card stays on Home until sync delivers its new status, so later clicks
// reopen the chat started for it. The promise is stored before the first
// await, so a second click can never observe an empty entry and send again
const startedChats = new Map<string, Promise<string | undefined>>();

const startChat = (
  entry: ChatPromptCardEntry,
  context: ReturnType<typeof actionCardAnalyticsContext>,
  analytics: ReturnType<typeof actionCardEventSnapshot>
): Promise<string | undefined> => {
  trackEvent('ACTION_CARD_CONFIRM_SUBMITTED', {
    ...analytics,
    action_intent: 'chat_prompt',
    experiment_id: entry.content.experiment_id,
    execution_kind: entry.content.execution.kind,
  });
  const { kind, value } = entry.content.execution;
  return performAttributedSend({
    threadId: NEW_CHAT_ID,
    mode: kind === 'mode' ? value : MAIN_CHAT_MODE,
    agent: kind === 'agent' ? value : undefined,
    content: entry.content.prompt,
    requestResponseAudio: false,
    originatingCardId: entry.card.id,
    cardAnalyticsContext: context,
  }).then((dispatch) => dispatch.createdThreadId ?? undefined);
};

export const startChatPromptCard = async (
  entry: ChatPromptCardEntry,
  openTab: OpenTab
): Promise<void> => {
  const context = actionCardAnalyticsContext('home');
  const analytics = actionCardEventSnapshot(entry.card, context);
  trackCardChatClicked(entry.card, 'home', 'prompt', context);
  const cardId = entry.card.id;
  let started = startedChats.get(cardId);
  if (!started) {
    started = startChat(entry, context, analytics);
    startedChats.set(cardId, started);
    // A failed send is not a started chat; let the next click try again
    started.catch(() => startedChats.delete(cardId));
  }
  const chatId = await started;
  if (!chatId) return;
  // The card is only rendered while still draft on the server, so every click
  // retries the completion. The endpoint is idempotent, so repeats are harmless
  void actionCardCommands
    .completeExternally(cardId)
    .then((outcome) => {
      const updated = littlebirdSdk.stores.actionCards.get(cardId);
      if (
        outcome.status === 'succeeded' &&
        updated &&
        (updated.status === 'completed' ||
          updated.status === 'completed_externally')
      )
        trackCardCompletedSnapshot(analytics, 'chat_handoff', updated.status);
    })
    .catch((error: unknown) =>
      logError(error, 'Failed to mark chat-prompt card done', {
        context: 'ChatPromptCard:complete',
        metadata: { cardId },
      })
    );
  openTab({ path: `/chats/${chatId}` });
};
